import { Addon, Color } from "@apis/index";
import Button from "@components/button";
import Column from "@components/column";
import Fields from "@components/fields";
import Row from "@components/row";
import { RootState } from "@store/index";
import { Form, Formik } from "formik";
import React, { Fragment, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

interface LensField {
  id: string;
  color: string;
  price: string;
  dbId?: number; // Store the actual database ID
}

interface FormValues {
  [key: string]: string;
}

interface NormalLens {
  id?: number;
  color: string;
  price: number | string;
}

interface PolarizedLens {
  id?: number;
  color: string;
  price: number | string;
}

interface ExistingData {
  normals: NormalLens[];
  polarizeds: PolarizedLens[];
}

const Lenscolor = ({ setLensColorData, existingData }: any) => {
  const { items: colours } = useSelector((state: RootState) => state.colors);
  const colors = colours?.map((color) => ({
    code: color.code,
    name: `${color.name} (${color.code})`,
  }));
  const [normalLensFields, setNormalLensFields] = useState<LensField[]>([]);
  const [polarizedLensFields, setPolarizedLensFields] = useState<LensField[]>(
    []
  );
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const fetchColors = useCallback(async () => {
    await Color.list({ state: "colors" });
  }, []);

  useEffect(() => {
    fetchColors();
  }, [fetchColors]);

  useEffect(() => {
    if (existingData && !isDataLoaded) {
      loadExistingData();
      setIsDataLoaded(true);
    }
  }, [existingData, isDataLoaded]);

  const loadExistingData = () => {
    if (existingData?.normals && existingData.normals.length > 0) {
      const normalFields = existingData.normals.map((normal, index) => ({
        id: normal.id ? `normal-${normal.id}` : `normal-existing-${index}`,
        color: normal.color || "",
        price: normal.price?.toString() || "",
        dbId: normal.id,
      }));
      setNormalLensFields(normalFields.slice(1));
    }

    if (existingData?.polarizeds && existingData.polarizeds.length > 0) {
      const polarizedFields = existingData.polarizeds.map(
        (polarized, index) => ({
          id: polarized.id
            ? `polarized-${polarized.id}`
            : `polarized-existing-${index}`,
          color: polarized.color || "",
          price: polarized.price?.toString() || "",
          dbId: polarized.id,
        })
      );
      setPolarizedLensFields(polarizedFields.slice(1));
    }
  };

  const addNormalLensField = () => {
    const newField: LensField = {
      id: `normal-${Date.now()}`,
      color: "",
      price: "",
    };
    setNormalLensFields((prev) => [...prev, newField]);
  };
  const addPolarizedLensField = () => {
    const newField: LensField = {
      id: `polarized-${Date.now()}`,
      color: "",
      price: "",
    };
    setPolarizedLensFields((prev) => [...prev, newField]);
  };
  const removeNormalLensField = async (id: string) => {
    // Find the field to check if it has a database ID
    const fieldToRemove = normalLensFields.find((field) => field.id === id);

    if (fieldToRemove?.dbId) {
      // If it has a database ID, remove from database
      console.log("Removing from database with ID:", fieldToRemove.dbId);
      try {
        await Addon.removeLensColor({
          state: "addons",
          id: fieldToRemove.dbId,
        });
        console.log("Successfully removed from database");
      } catch (error) {
        console.error("Failed to remove from database:", error);
        return; // Don't remove from state if API call fails
      }
    } else {
      console.log("Removing newly added field (no database ID)");
    }

    // Remove from client state
    setNormalLensFields((prev) => prev.filter((field) => field.id !== id));
  };

  const removePolarizedLensField = async (id: string) => {
    // Find the field to check if it has a database ID
    const fieldToRemove = polarizedLensFields.find((field) => field.id === id);

    if (fieldToRemove?.dbId) {
      // If it has a database ID, remove from database
      console.log("Removing from database with ID:", fieldToRemove.dbId);
      try {
        await Addon.removeLensColor({
          state: "addons",
          id: fieldToRemove.dbId,
        });
        console.log("Successfully removed from database");
      } catch (error) {
        console.error("Failed to remove from database:", error);
        return; // Don't remove from state if API call fails
      }
    } else {
      console.log("Removing newly added field (no database ID)");
    }

    // Remove from client state
    setPolarizedLensFields((prev) => prev.filter((field) => field.id !== id));
  };

  const buildLensColorData = (values: FormValues) => {
    const normals = [];
    const polarized = [];

    // Add initial normal lens (check if it exists in existing data)
    if (values.normallenscolor || values.normallensprice) {
      const firstNormalDbId = existingData?.normals?.[0]?.id;
      normals.push({
        id: firstNormalDbId, // Include database ID for updates
        color: values.normallenscolor || "",
        price: values.normallensprice || "",
      });
    }

    // Add dynamic normal lenses
    normalLensFields.forEach((field) => {
      const color = values[`normallenscolor-${field.id}`];
      const price = values[`normallensprice-${field.id}`];
      if (color || price) {
        normals.push({
          id: field.dbId, // Include database ID for updates
          color: color || "",
          price: price || "",
        });
      }
    });

    // Add initial polarized lens (check if it exists in existing data)
    if (values.polarizedlenscolor || values.polarizedlensprice) {
      const firstPolarizedDbId = existingData?.polarizeds?.[0]?.id;
      polarized.push({
        id: firstPolarizedDbId, // Include database ID for updates
        color: values.polarizedlenscolor || "",
        price: values.polarizedlensprice || "",
      });
    }

    // Add dynamic polarized lenses
    polarizedLensFields.forEach((field) => {
      const color = values[`polarizedlenscolor-${field.id}`];
      const price = values[`polarizedlensprice-${field.id}`];
      if (color || price) {
        polarized.push({
          id: field.dbId, // Include database ID for updates
          color: color || "",
          price: price || "",
        });
      }
    });

    return { normals, polarized };
  };

  const getInitialValues = () => {
    const initialValues: FormValues = {};

    if (existingData?.normals && existingData.normals.length > 0) {
      // Set initial normal lens values
      const firstNormal = existingData.normals[0];
      initialValues.normallenscolor = firstNormal.color || "";
      initialValues.normallensprice = firstNormal.price?.toString() || "";

      // Set dynamic normal lens values
      existingData.normals.slice(1).forEach((normal, index) => {
        const fieldId = normal.id
          ? `normal-${normal.id}`
          : `normal-existing-${index}`;
        initialValues[`normallenscolor-${fieldId}`] = normal.color || "";
        initialValues[`normallensprice-${fieldId}`] =
          normal.price?.toString() || "";
      });
    }

    if (existingData?.polarizeds && existingData.polarizeds.length > 0) {
      // Set initial polarized lens values
      const firstPolarized = existingData.polarizeds[0];
      initialValues.polarizedlenscolor = firstPolarized.color || "";
      initialValues.polarizedlensprice = firstPolarized.price?.toString() || "";

      // Set dynamic polarized lens values
      existingData.polarizeds.slice(1).forEach((polarized, index) => {
        const fieldId = polarized.id
          ? `polarized-${polarized.id}`
          : `polarized-existing-${index}`;
        initialValues[`polarizedlenscolor-${fieldId}`] = polarized.color || "";
        initialValues[`polarizedlensprice-${fieldId}`] =
          polarized.price?.toString() || "";
      });
    }

    return initialValues;
  };

  const LensFieldGroup = ({
    type,
    fields,
    onAdd,
    onRemove,
  }: {
    type: "normal" | "polarized";
    fields: LensField[];
    onAdd: () => void;
    onRemove: (id: string) => void;
  }) => {
    const prefix = type === "normal" ? "normal" : "polarized";
    const title = type === "normal" ? "Normal Lens" : "Polarized Lens";
    const icon = type === "normal" ? "👁️" : "🕶️";

    return (
      <div className="lens-card">
        <div className="card-header">
          <div className="card-title">
            <span className="card-icon">{icon}</span>
            {title}
            {existingData && (
              <span className="data-count">
                (
                {type === "normal"
                  ? existingData.normals?.length || 0
                  : existingData.polarizeds?.length || 0}{" "}
                colors)
              </span>
            )}
          </div>
          <button type="button" onClick={onAdd} className="add-btn">
            <span className="btn-icon">+</span>
            Add Color
          </button>
        </div>

        <div className="card-content">
          {/* Initial field */}
          <div className="form-row">
            <div className="field-combination">
              <Fields.DropDown
                name={`${prefix}lenscolor`}
                options={colors}
                titleAsKey="name"
                valueAsKey="name"
                colorAsKey="code"
                showColor={true}
                label="Lens Color"
              />
              <Fields.Input
                name={`${prefix}lensprice`}
                label="Price"
                type="number"
              />
            </div>
            <div className="action-spacer" />
          </div>

          {/* Dynamic fields */}
          {fields.map((field) => (
            <div key={field.id} className="form-row dynamic-row">
              <div className="field-combination">
                <Fields.DropDown
                  name={`${prefix}lenscolor-${field.id}`}
                  options={colors}
                  titleAsKey="name"
                  valueAsKey="name"
                  colorAsKey="code"
                  showColor={true}
                  label="Lens Color"
                />
                <Fields.Input
                  name={`${prefix}lensprice-${field.id}`}
                  label="Price"
                  type="number"
                />
              </div>
              <button
                type="button"
                onClick={() => onRemove(field.id)}
                className="remove-btn"
              >
                ×
              </button>
              {/* Show database ID for existing records */}
              {field.dbId && (
                <div
                  style={{
                    fontSize: "10px",
                    color: "#666",
                    marginTop: "5px",
                    position: "absolute",
                    right: "60px",
                    top: "5px",
                  }}
                >
                  DB ID: {field.dbId}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <Formik
        initialValues={getInitialValues()}
        onSubmit={(values) => {
          const formData = buildLensColorData(values);
          console.log("Lens Color Data on submit:", formData);
          if (setLensColorData) {
            setLensColorData(formData);
          }
        }}
        enableReinitialize={true}
      >
        {({ submitForm, values }) => {
          useEffect(() => {
            const formData = buildLensColorData(values);
            if (setLensColorData) {
              setLensColorData(formData);
            }
          }, [values, setLensColorData]);

          return (
            <Form>
              <div className="lens-container">
                <div className="header-section">
                  <h1 className="page-title">
                    {existingData
                      ? "Update Lens Configuration"
                      : "Lens Configuration"}
                  </h1>
                  <p className="page-subtitle">
                    {existingData
                      ? "Update normal and polarized lens colors and pricing"
                      : "Manage normal and polarized lens colors and pricing"}
                  </p>

                  {existingData && (
                    <div className="existing-data-info">
                      <div className="info-badge">
                        📊 Loaded: {existingData.normals?.length || 0} normal
                        colors, {existingData.polarizeds?.length || 0} polarized
                        colors
                      </div>
                    </div>
                  )}
                </div>

                <div className="cards-grid">
                  <LensFieldGroup
                    type="normal"
                    fields={normalLensFields}
                    onAdd={addNormalLensField}
                    onRemove={removeNormalLensField}
                  />

                  <LensFieldGroup
                    type="polarized"
                    fields={polarizedLensFields}
                    onAdd={addPolarizedLensField}
                    onRemove={removePolarizedLensField}
                  />
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>

      <style>{`
        .lens-container {
          max-width: 100%;
          margin: 0 auto;
          padding: 2rem;
        }

        .header-section {
          text-align: center;
          margin-bottom: 3rem;
        }

        .page-title {
          font-size: 2.5rem;
          font-weight: 300;
          color: #1a1a1a;
          margin: 0 0 0.5rem 0;
          letter-spacing: -0.02em;
        }

        .page-subtitle {
          font-size: 1.1rem;
          color: #666;
          margin: 0;
          font-weight: 400;
        }

        .existing-data-info {
          margin-top: 1rem;
        }

        .info-badge {
          display: inline-block;
          background: #e3f2fd;
          color: #1976d2;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .data-count {
          font-size: 0.9rem;
          color: #666;
          margin-left: 0.5rem;
          font-weight: 400;
        }

        .cards-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          margin-bottom: 3rem;
        }

        .lens-card {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid rgba(0, 0, 0, 0.05);
          transition: all 0.3s ease;
          position: relative;
        }

        .lens-card:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
          transform: translateY(-2px);
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        }

        .card-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.5rem;
          font-weight: 500;
          color: #1a1a1a;
        }

        .card-icon {
          font-size: 1.75rem;
        }

        .add-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 0.75rem 1.5rem;
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 500;
          transition: all 0.3s ease;
        }

        .add-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
        }

        .btn-icon {
          font-size: 1.2rem;
          font-weight: 300;
        }

        .card-content {
          space-y: 1.5rem;
        }

        .form-row {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          margin-bottom: 1.5rem;
          position: relative;
        }

        .dynamic-row {
          animation: slideIn 0.3s ease-out;
        }

        .field-combination {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 2rem;
          flex: 1;
        }

        .action-spacer {
          width: 48px;
          visibility: hidden;
        }

        .remove-btn {
          width: 48px;
          height: 48px;
          background: rgba(220, 53, 69, 0.1);
          color: #dc3545;
          border: none;
          border-radius: 12px;
          cursor: pointer;
          font-size: 1.5rem;
          font-weight: 300;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: 1.75rem;
        }

        .remove-btn:hover {
          background: #dc3545;
          color: white;
          transform: scale(1.05);
        }

        .action-section {
          display: flex;
          justify-content: center;
          padding-top: 2rem;
          border-top: 1px solid rgba(0, 0, 0, 0.08);
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        /* Responsive design */
        @media (max-width: 968px) {
          .cards-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
          
          .lens-container {
            padding: 1rem;
          }
          
          .page-title {
            font-size: 2rem;
          }
        }

        @media (max-width: 640px) {
          .card-header {
            flex-direction: column;
            gap: 1rem;
            align-items: stretch;
          }
          
          .field-combination {
            grid-template-columns: 1fr;
          }
          
          .form-row {
            flex-direction: column;
          }
          
          .remove-btn {
            align-self: flex-end;
            margin-top: 0.5rem;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default React.memo(Lenscolor);
